// src/components/practice/tasks/MatchingHeadlinesToOpinions.jsx
import React from 'react';
import { marked } from 'marked';
import { FlagButton } from '../PartDisplay';
import MatchingQuestion from '../MatchingQuestion';
import { X } from 'lucide-react';

const MatchingHeadlinesToOpinions = ({
  partData,
  questions,
  examples,
  partAnswers,
  handleBatchAnswerChange,
  flaggedQuestions,
  toggleFlagQuestion,
  isMobileAnswerSheetOpen,
  onCloseMobileAnswerSheet,
  ...props
}) => {
  const headlines = partData.content.headlines || [];
  const opinions = partData.content.texts || [];

  // Logic to handle strikethrough and disabled options
  const realAnswers = Object.values(partAnswers);
  const exampleAnswers = examples.map(ex => ex.correctAnswer);
  const usedOpinionIds = new Set([...realAnswers, ...exampleAnswers]);


  // 1. Map Headlines (Questions) - Unchanged
  const mqQuestions = questions
    .map(q => {
      const headline = headlines.find(h => h.id === String(q.questionNumber));
      if (!headline) return null;
      return {
        questionId: q.questionId,
        number: q.questionNumber,
        text: headline.text,
      };
    })
    .filter(h => h);

  // 2. Map Opinions (Options)
  // --- MODIFICATION: Removed .filter(op => !op.isExample) ---
  const mqOptions = opinions
    .map(op => ({
      id: op.id,
      text: `Text ${op.id.toUpperCase()}: ${op.author}` // Full text for the popup
    }));
  // --- END MODIFICATION ---

  // 3. Add the special "no match" option
  const specialOption = { id: partData.examProvider === 'TELC' ? 'x' : '0' };
  const allOptions = [...mqOptions, specialOption];


  // --- LEFT COLUMN CONTENT (Reading) ---
  const leftContent = (
    <div className="space-y-6">


      {/* Examples --- REMOVED from here --- */}

      {/* Opinions (The reading text) */}
      <div className="border-2 border-gray-900 bg-white p-6 rounded-none">
        <h3 className="text-xl font-bold text-gray-900 border-b-2 border-gray-300 pb-3 mb-4">
          Meinungen
        </h3>
       <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
  {opinions.map(op => {
    const isUsed = usedOpinionIds.has(op.id);
    return (
      <div
        key={op.id}
        className="relative p-5 border-2 border-gray-300 rounded-xl bg-white shadow-sm h-full flex flex-col"
      >
        {/* Letter badge at top left */}
        <div className="absolute -top-3 -left-3 w-10 h-10 bg-white border-2 border-gray-900 rounded-lg flex items-center justify-center shadow-md">
          <span className={`font-bold text-lg ${isUsed ? 'line-through text-gray-400' : 'text-gray-900'}`}>
            {op.id}
          </span>
        </div>

        {/* Text content - justified */}
        <p className={`text-sm text-gray-800 leading-relaxed text-justify flex-1 ${isUsed ? 'text-gray-400' : ''}`}>
          {op.content}
        </p>

        {/* Author name at bottom right */}
        <p className={`text-xs italic text-gray-600 text-right mt-3 ${isUsed ? 'text-gray-400' : ''}`}>
          {op.author}
        </p>
      </div>
    );
  })}
</div>
      </div>
    </div>
  );

  // --- RIGHT COLUMN CONTENT (Answering) ---
  const rightContent = (
    <div>
      {/* Answer Sheet Header (unchanged) */}
      <div className="border-2 border-gray-900 bg-white p-3 rounded-none mb-4">
        <h3 className="text-lg font-bold text-gray-900 uppercase tracking-wide">
          Antwortbogen
        </h3>
        <p className="text-xs text-gray-600 mt-1">
          Welche Überschrift passt zu welcher Meinung?
        </p>
      </div>

      <div className="border-2 border-gray-900 bg-white rounded-none">

        {/* Examples rendered inside the answer box (Unchanged) */}
        {examples.map((ex, idx) => {
          const headline = headlines.find(h => h.id == ex.questionNumber);
          const exampleText = headline
            ? headline.text
            : (ex.questionText || ex.statement || "Beispiel-Überschrift");

          const correctAdText = ex.correctAnswer ? ex.correctAnswer.toUpperCase() : '?';

          return (
            <div
              key={`ex-${idx}`}
              className="bg-yellow-50 p-4 opacity-90 border-b border-gray-300"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 flex items-start">
                  <span className="font-bold text-sm mr-2 pt-0.5">
                    {ex.questionNumber || '0'}.
                  </span>
                  <span className="text-sm leading-relaxed">
                    {exampleText}
                  </span>
                </div>
                <div className="ml-4 flex-shrink-0">
                  <div
                    className="border-2 border-gray-900 bg-gray-900 text-white font-bold text-sm px-3 py-1.5 rounded-none cursor-not-allowed"
                    style={{ minWidth: '70px', textAlign: 'center' }}
                  >
                    {correctAdText}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Regular Questions (Unchanged) */}
        <div className="p-4">
          <MatchingQuestion
            questions={mqQuestions}
            options={allOptions}
            savedAnswers={partAnswers}
            onAnswerChange={handleBatchAnswerChange} // Use batch handler
            title="Meinung wählen"
            flaggedQuestions={flaggedQuestions}
            toggleFlagQuestion={toggleFlagQuestion}
            exampleAnswers={exampleAnswers} // Pass example answers to disable them
          />
        </div>
      </div>
    </div>
  );

  const mobileOverlay = (isMobileAnswerSheetOpen && onCloseMobileAnswerSheet) ? (
    <div className="fixed inset-0 z-[60] flex flex-col justify-end md:hidden" aria-modal="true" role="dialog" aria-label="Antwortbogen">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCloseMobileAnswerSheet} aria-hidden />
      <div className="relative bg-white rounded-t-2xl shadow-2xl max-h-[90vh] flex flex-col animate-slide-up">
        <div className="flex items-center justify-between flex-shrink-0 p-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl">
          <h3 className="text-lg font-bold text-gray-900">Antwortbogen</h3>
          <button type="button" onClick={onCloseMobileAnswerSheet} className="p-2 -m-2 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors touch-manipulation min-w-[48px] min-h-[48px] flex items-center justify-center" aria-label="Schließen">
            <X className="w-6 h-6 text-gray-700" />
          </button>
        </div>
        <div className="overflow-y-auto overscroll-contain p-4 pb-safe">{rightContent}</div>
      </div>
    </div>
  ) : null;

  return (
    <div className="w-full max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-6">
      <div className="grid grid-cols-1 md:grid-cols-5 md:gap-8">
        <div className="md:col-span-3 min-w-0">
          {leftContent}
        </div>
        <div className="hidden md:block md:col-span-2 mt-6 md:mt-0">
          <div className="md:sticky md:top-6">
            {rightContent}
          </div>
        </div>
      </div>
      {mobileOverlay}
    </div>
  );
};

export default MatchingHeadlinesToOpinions;